import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivePageService } from '@services/active-page/active-page.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { QuestionnaireConfig } from 'app/@types/dto/questionnaire-config.model';
import { PageDto } from 'app/@types/dto/page-dto.model';
import { Select, Store } from '@ngxs/store';
import { QuestionnaireConfigState } from '@state/questionnaire.config.state';
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs';
import { QuestionInstanceDto } from 'app/@types/dto/question-instance-dto.model';
import { QuestionDto } from 'app/@types/dto/question-dto.model';
import { SectionDto } from 'app/@types/dto/section-dto.model';
import { QuestionCriteriaDto } from 'app/@types/dto/question-criteria-dto.model';
import { ToastService } from '@services/common/toast.service';
import { QuestionnaireState } from '@state/questionnaire.state';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { OrganizationState } from '@state/organization.state';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';

@Component({
    selector: 'app-common-page',
    templateUrl: './common-page.component.html',
    styleUrls: ['./common-page.component.scss'],
    standalone: false,
})
export class CommonPageComponent implements OnInit, OnDestroy {
    @Select(QuestionnaireConfigState.getStaticConfig) config$!: Observable<QuestionnaireConfig>;
    @Select(QuestionnaireState.getQuestionInstances) questionInstancePerQuestionId$!: Observable<
        Map<string, QuestionInstanceDto[]>
    >;
    @Select(QuestionnaireState.getExternalUpdate) externalUpdates$!: Observable<Set<string>>;
    @Select(QuestionnaireState.getQuestionnaire) questionnaire$!: Observable<QuestionnaireDto>;
    @Select(OrganizationState.getEntities) entities$!: Observable<OrganizationEntityDto[]>;
    @Select(OrganizationState.getOrganization) organization$!: Observable<OrganizationDto>;

    private ngUnsubscribe = new Subject<void>();
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public activePage: PageDto;
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public config: QuestionnaireConfig;
    private questionInstancePerQuestionCode: Map<string, QuestionInstanceDto[]> = new Map();
    private questionPerQuestionCode: Map<string, QuestionDto> = new Map();
    private hasBeenFTEwarned: boolean = false;
    public externalUpdates: Set<string> = new Set();
    public isLoaded: boolean = false;
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public questionnaire: QuestionnaireDto;
    public entities: OrganizationEntityDto[] = [];
    public activeEntity: OrganizationEntityDto | undefined;
    public isInit: boolean = false;
    public currentEntityFte: number = 0;
    public organizationId: string = '';
    public questionnaireId: string = '';
    public locale: string = '';

    //Constructor
    constructor(
        private activePageService: ActivePageService,
        public translateService: CustomTranslateService,
        private store: Store,
        private toastService: ToastService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        combineLatest([
            this.activePageService.getActivePage(),
            this.config$,
            this.questionInstancePerQuestionId$,
            this.questionnaire$,
            this.entities$,
            this.organization$,
        ])
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(([page, config, questionInstancePerQuestionCode, questionnaire, entities, organization]) => {
                if (
                    page &&
                    config &&
                    questionInstancePerQuestionCode &&
                    questionnaire &&
                    questionnaire.id &&
                    entities &&
                    organization &&
                    organization.uid
                ) {
                    this.activePage = page;
                    this.config = config;
                    this.organizationId = organization.uid;
                    this.questionnaireId = questionnaire.id;
                    this.questionPerQuestionCode = this.config.pages.reduce((map, page) => {
                        {
                            Object.values(this.config.questionsPerPage[page.pageCode])
                                .flatMap((qa) => qa)
                                .forEach((q) => map.set(q.questionCode, q));
                            return map;
                        }
                    }, new Map<string, QuestionDto>());
                    this.questionInstancePerQuestionCode = questionInstancePerQuestionCode;
                    this.questionnaire = questionnaire;
                    this.entities = entities.filter((entity) => !entity.deleted);
                    this.currentEntityFte =
                        entities.find((e) => e.id === this.questionnaire.organizationEntityId)?.employeesCount ?? 0;
                    this.activeEntity = this.entities.find(
                        (entity) => entity.id === this.questionnaire.organizationEntityId,
                    );
                    this.locale = this.translateService.currentLang;
                    this.handleSectionsOptions();
                    this.isInit = true;
                }
            });

        this.translateService.onLangChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe((_langChangeEvent) => {
            this.locale = this.translateService.currentLang;
        });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
    }

    handleSectionsOptions(): void {
        const questionInstances = Array.from(this.questionInstancePerQuestionCode.values()).flatMap((a) => a);
        const questions = Array.from(this.questionPerQuestionCode.values());
        this.activePage.sections.forEach((s) => {
            if (s.options?.computeTot) {
                s.total = this.handleSectionTotal(s, questionInstances, questions);
            }
            if (s.options?.fteMatch) {
                this.handleFteMatching(this.activePage, s, questionInstances, questions);
            }
        });
    }

    handleSectionTotal(
        section: SectionDto,
        _questionInstances: QuestionInstanceDto[],
        questions: QuestionDto[],
    ): number {
        let tot: number = 0;
        if (section.options?.computeTot && section.options?.computeTot.length !== 0) {
            section.options?.computeTot.forEach((ct) => {
                const relevantQuestions = questions.filter((q) =>
                    q.pageCode ? q.pageCode === ct.pageCode : ct.sectionCode ? q.pageSection === ct.sectionCode : false,
                );
                relevantQuestions.forEach((q) => {
                    if (q.value.domainValue1) {
                        const instances = this.questionInstancePerQuestionCode.get(q.questionCode);
                        if (instances && instances.length !== 0) {
                            instances.forEach((qi) => {
                                if (qi.unit === ct.enumFilterCriteria && qi.value.value1 && qi.value.value2) {
                                    tot += ct.factor
                                        ? Number.parseFloat(qi.value.value2) * ct.factor
                                        : Number.parseFloat(qi.value.value2);
                                }
                            });
                        }
                    }
                });
            });
        }
        return tot;
    }

    handleFteMatching(
        page: PageDto,
        section: SectionDto,
        questionInstances: QuestionInstanceDto[],
        _questions: QuestionDto[],
    ): void {
        if (this.activeEntity && this.activeEntity.employeesCount && !this.hasBeenFTEwarned) {
            const questionCodes = new Set(
                this.config.questionsPerPage[page.pageCode][section.sectionCode].map((q) => q.questionCode),
            );

            let sectionReportedEmployees: number = 0;

            questionInstances.forEach((qi) => {
                if (
                    questionCodes.has(qi.questionCode) &&
                    qi.value.value1 &&
                    !isNaN(Number.parseFloat(qi.value.value1))
                ) {
                    sectionReportedEmployees += Number.parseFloat(qi.value.value1);
                }
            });

            if (this.activeEntity.employeesCount < sectionReportedEmployees) {
                this.toastService.showWarning({
                    details:
                        this.translateService.instant(`section.${section.sectionCode}.title`) +
                        '. \n' +
                        this.translateService.instant('common.excessInputCompareToFTE') +
                        ': ' +
                        this.activeEntity.employeesCount +
                        '<' +
                        sectionReportedEmployees,
                });
                this.hasBeenFTEwarned = true;
            }
        }
    }

    handleQuestionCriteria(hideQuestionCriteria: QuestionCriteriaDto): boolean {
        const instances = this.questionInstancePerQuestionCode.get(hideQuestionCriteria.questionCode);

        return !(
            instances !== undefined &&
            instances.length !== 0 &&
            instances.some((i) => i.value.value1 === hideQuestionCriteria.questionValue)
        );
    }

    getTooltipContent(sectionCode: string): string {
        const text = this.translateService.keyExists(`section.${sectionCode}.infoType.text`)
            ? this.translateService.instant(`section.${sectionCode}.infoType.text`)
            : '';

        const whereToFind = this.translateService.keyExists(`section.${sectionCode}.infoType.whereToFind`)
            ? this.translateService.instant(`section.${sectionCode}.infoType.whereToFind`)
            : '';

        let tooltipContent = '';

        if (text) {
            tooltipContent += `<p style="margin: 0;">${text}</p>`;
        }

        if (text && whereToFind) {
            tooltipContent += `<hr style="margin: 0.5rem 0;">`; // Adds a separator
        }

        if (whereToFind) {
            tooltipContent += `<p style="margin: 0;">${whereToFind}</p>`;
        }

        return tooltipContent;
    }
}
